import { PureQueryOptions } from 'apollo-client'
import React, { useState } from 'react'
import { LibraryAgentLanguage } from '../library/library-agent-language'
import { ShareByEmailType } from '../../helpers/ui-interfaces'
import { ExportLibrariesAction } from './export-action'
import { SendAction } from './send-action'
import {
  ModifyLibrariesTagsAction,
  ModifyLibrariesTagsByFilterAction
} from './tag-action'
import { UpdateStatusesWithHashesAction } from './update-statuses-actions'
import {
  BulkDeleteLibrariesModal,
  DeleteLibrariesType,
  FilterLibrariesActionType
} from './bulk-delete-modal'
import { BulkDeleteLibrariesAction } from './bulk-delete-action'
import { ActionButtonGroup } from '@contrast-security-inc/design-system-actions'
import { hasRole } from 'contrastBase/RoleSwitch'
import { Role } from '@contrast-security-inc/base-types'

export interface Library {
  id: string
  hash: string
  language: LibraryAgentLanguage
  name: string
  tags: string[]
  applications: Array<{ appId: string }>
}

export interface FilteredActionProps {
  hasAppEditPermission: boolean
  organizationID: string
  applicationID?: string
  serverID?: string
  organizationRole: Role
  libraries: Library[]
  excludedLibraries: string[]
  filters: {
    filterText: string
    quickFilter: string
    aggregations: Record<string, string[]>
  }
  refresh: () => Promise<any>
  refetchQueries?: PureQueryOptions[]
  showStatusAction?: boolean
  operationType?: FilterLibrariesActionType
}

export function FilteredActionList({
  hasAppEditPermission,
  organizationID,
  applicationID,
  serverID,
  organizationRole,
  excludedLibraries,
  filters,
  refetchQueries,
  refresh,
  libraries,
  showStatusAction,
  operationType = FilterLibrariesActionType.BY_FILTERS
}: FilteredActionProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const showDisabled = hasRole(organizationRole, 'EDIT')

  return (
    <ActionButtonGroup>
      {showStatusAction && applicationID && hasAppEditPermission ? (
        <UpdateStatusesWithHashesAction
          organizationID={organizationID}
          libraries={libraries}
          refetchQueries={refetchQueries}
          refresh={refresh}
          applicationID={applicationID}
        />
      ) : null}
      {operationType === FilterLibrariesActionType.BY_HASHES ? (
        <ModifyLibrariesTagsAction
          libraries={libraries}
          applicationRole={organizationRole}
          organizationID={organizationID}
          refetchQueries={refetchQueries}
          refresh={refresh}
          bulk
        />
      ) : (
        <ModifyLibrariesTagsByFilterAction
          filters={filters}
          organizationID={organizationID}
          libraries={libraries}
          excludedHashes={excludedLibraries}
          refetchQueries={refetchQueries}
          refresh={refresh}
        />
      )}
      <ExportLibrariesAction
        selectedLibraryHashes={libraries.map((l) => l.hash)}
        organizationID={organizationID}
        applicationID={applicationID}
        serverID={serverID}
        filters={filters}
        excludedHashes={excludedLibraries}
      />
      <SendAction
        showDisabled={showDisabled}
        role={organizationRole}
        bulk
        organizationID={organizationID}
        refetchQueries={refetchQueries}
        refresh={refresh}
        bugtrackerDisabled
        filters={filters}
        libraries={libraries}
        libraryHashesToExclude={excludedLibraries}
        shareType={
          operationType === FilterLibrariesActionType.BY_FILTERS
            ? ShareByEmailType.BY_FILTERS
            : ShareByEmailType.BY_HASHES
        }
      />
      <BulkDeleteLibrariesAction
        organizationRole={organizationRole}
        showDisabled={showDisabled}
        libraries={libraries}
        setShowDeleteModal={setShowDeleteModal}
      />
      {showDeleteModal && (
        <BulkDeleteLibrariesModal
          deleteType={
            operationType === FilterLibrariesActionType.BY_FILTERS
              ? DeleteLibrariesType.BY_FILTERS
              : DeleteLibrariesType.BY_HASHES
          }
          organizationID={organizationID}
          applicationID={applicationID}
          serverID={serverID}
          refetchQueries={refetchQueries}
          onSuccess={refresh}
          organizationRole={organizationRole}
          showDisabled={showDisabled}
          libraryHashesToExclude={excludedLibraries}
          filters={filters}
          libraries={libraries}
          setShowDeleteModal={setShowDeleteModal}
        />
      )}
    </ActionButtonGroup>
  )
}
